// src/components/kuestion/campus/StudentReport.jsx
//
// One student's full record: latest proctored result, strongest and weakest
// module, trend across attempts, the module breakdown, and the PDF build.
// The answer key for proctored papers is never part of any of it.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useCampus } from './CampusContext';
import { Loading, Alert, Stat, RuleHead, ModuleRow, Note } from '../shared/Bits';
import { ClockIcon } from '../shared/Icons';
import { fmtDate, fullName, isProctored, modeLabel } from '../shared/format';
import { kq } from '../../../config/routes';

export default function StudentReport() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { api, students, assignments, reports, requestReport } = useCampus();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const student = useMemo(() => students.find((s) => String(s.id) === String(id)) || null, [students, id]);

  useEffect(() => {
    let live = true;
    setData(null);
    api.get(API_ENDPOINTS.CAMPUS_ANALYTICS_STUDENT(id))
      .then((d) => { if (live) setData(d || { modules: [] }); })
      .catch((e) => { if (live) { setError(e.message); setData({ modules: [] }); } });
    return () => { live = false; };
  }, [api, id]);

  const modules = data?.modules || [];
  const min = data?.min_questions_for_strength;

  // Sittings: from the analytics payload when present, else derived from the
  // assignment list's per-student status.
  const sittings = useMemo(() => {
    if (Array.isArray(data?.sittings) && data.sittings.length) {
      // `passed` is true/false/null and `pass_percentage` the threshold snapshotted at start (v4 §6).
      return data.sittings.map((s, i) => ({ key: s.id ?? i, name: s.template_name || s.exam_name, mode: s.mode, at: s.submitted_at || s.completed_at, score: s.score ?? s.scored, total: s.total, pct: s.percentage ?? s.percent, pass: s.passed, passMark: s.pass_percentage ?? s.pass_mark }));
    }
    const out = [];
    assignments.forEach((a) => {
      const row = (a.students || []).find((s) => String(s.student_id ?? s.id) === String(id));
      if (row && (row.status === 'submitted' || row.submitted_at)) out.push({ key: `${a.id}`, name: a.template_name, mode: a.mode, at: row.submitted_at, score: row.score, total: row.total, pct: row.percentage });
    });
    return out.sort((x, y) => new Date(y.at || 0) - new Date(x.at || 0));
  }, [data, assignments, id]);

  const scored = modules.filter((m) => m.sufficient !== false && m.strength != null);
  const strongest = scored.length ? scored.reduce((a, b) => (Number(b.strength) > Number(a.strength) ? b : a)) : null;
  const weakest = scored.length ? scored.reduce((a, b) => (Number(b.strength) < Number(a.strength) ? b : a)) : null;
  const latestProctored = sittings.find((s) => isProctored(s.mode));
  const pctOf = (s) => (s.pct != null ? Number(s.pct) : s.score != null && s.total ? (s.score / s.total) * 100 : null);
  const chrono = [...sittings].reverse().map(pctOf).filter((v) => v != null);
  const trend = chrono.length >= 2 ? Math.round(chrono[chrono.length - 1] - chrono[0]) : null;

  const key = `student:${id}`;
  const job = reports.find((r) => r.key === key);
  const label = fullName(student) || `Student ${id}`;

  return (
    <div className="kqc-page kqc-page--mid">
      <button type="button" className="btn btn-ghost kqc-back" onClick={() => navigate(kq(`students/${id}`))}>&larr; Student</button>
      <div className="kqc-pagehead" style={{ marginBottom: 24 }}>
        <div style={{ minWidth: 0 }}>
          <div className="kqc-kicker" style={{ marginBottom: 8 }}>Student report</div>
          <h1 className="kqc-h1" style={{ marginBottom: 8 }}>{label}</h1>
          <div className="kqc-small kqc-muted">{student?.login_id} · {sittings.length} sitting{sittings.length === 1 ? '' : 's'}{student?.created_at ? ` · joined ${fmtDate(student.created_at)}` : ''}</div>
        </div>
        <span className="kqc-spacer" />
        <button type="button" className="btn btn-primary" disabled={(job?.status === 'building' || job?.status === 'queued')} onClick={() => requestReport(key, label, API_ENDPOINTS.CAMPUS_REPORT_STUDENT(id))}>{(job?.status === 'building' || job?.status === 'queued') ? 'Building…' : 'Build the PDF'}</button>
      </div>

      {error && <Alert kind="error">{error}</Alert>}
      {!data ? <Loading label="Loading report" /> : (
        <>
          <div className="kqc-stats" style={{ marginBottom: 28 }}>
            <Stat label="Latest proctored" value={latestProctored ? (latestProctored.pass != null ? (latestProctored.pass ? 'PASS' : 'FAIL') : pctOf(latestProctored) != null ? `${Math.round(pctOf(latestProctored))}%` : 'Sat') : '—'} accent={latestProctored && latestProctored.pass !== false} hint={latestProctored ? `${latestProctored.score != null && latestProctored.total != null ? `${latestProctored.score} / ${latestProctored.total}` : latestProctored.name || ''}${latestProctored.passMark != null ? ` · pass mark ${latestProctored.passMark}%` : latestProctored.pass == null ? ' · no pass mark' : ''}` : 'No proctored sitting yet'} />
            <Stat label="Strongest" value={strongest ? strongest.module : '—'} size="md" hint={strongest ? `${Math.round(strongest.strength)}% over ${strongest.attempted} questions` : 'Not enough data'} />
            <Stat label="Weakest" value={weakest ? weakest.module : '—'} size="md" accent={weakest ? 'ink' : null} hint={weakest ? `${Math.round(weakest.strength)}% over ${weakest.attempted} questions` : 'Not enough data'} />
            <Stat label="Trend" value={trend == null ? '—' : `${trend > 0 ? '+' : ''}${trend}`} hint={trend == null ? 'Needs two or more attempts' : `Points across ${chrono.length} attempts`} />
          </div>

          <div className="kqc-cols">
            <div className="is-main">
              <RuleHead title="Module breakdown" />
              {modules.length === 0 ? (
                <div className="kqc-empty">No scored questions yet.</div>
              ) : (
                <div className="kqc-list">
                  {modules.map((m) => <ModuleRow key={m.module} m={m} min={min} stacked />)}
                </div>
              )}

              {sittings.length > 0 && (
                <div style={{ marginTop: 28 }}>
                  <RuleHead title="Sittings" />
                  <div className="table-wrap">
                    <table className="table" style={{ minWidth: 480 }}>
                      <thead><tr><th>Exam</th><th>Mode</th><th>Sat</th><th>Score</th></tr></thead>
                      <tbody>
                        {sittings.map((s) => (
                          <tr key={s.key}>
                            <td>{s.name}</td>
                            <td><span className={`tag ${isProctored(s.mode) ? 'tag-accent' : 'tag-neutral'}`}>{modeLabel(s.mode)}</span></td>
                            <td>{fmtDate(s.at)}</td>
                            <td><strong>{s.score != null && s.total != null ? `${s.score} / ${s.total}` : pctOf(s) != null ? `${Math.round(pctOf(s))}%` : '—'}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="is-side" style={{ maxWidth: 400 }}>
              <RuleHead title="The PDF" />
              <div className="kqc-box kqc-box--surface">
                <div className="kqc-box__body">
                  <Note icon={<ClockIcon />} style={{ marginBottom: 16, background: 'transparent', border: 0, padding: 0, maxWidth: 'none', fontSize: 13 }}>Rendering is the heaviest job in the platform, so it runs in the background. Ask for it and carry on — it appears in your report list when it&rsquo;s done.</Note>
                  <div className="kqc-box__rows" style={{ marginBottom: 16 }}>
                    <div><span>Every sitting, both modes</span><strong>Included</strong></div>
                    <div><span>Module strengths and trend</span><strong>Included</strong></div>
                    <div><span>Answer key for proctored papers</span><strong className="kqc-accent-ink">Never</strong></div>
                  </div>
                  {job?.status === 'ready' ? (
                    <a href={job.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-block" style={{ minHeight: 44 }}>Download the PDF</a>
                  ) : job?.status === 'failed' ? (
                    <>
                      <Alert kind="error">{job.error}</Alert>
                      <button type="button" className="btn btn-primary btn-block" style={{ minHeight: 44 }} onClick={() => requestReport(key, label, API_ENDPOINTS.CAMPUS_REPORT_STUDENT(id))}>Try again</button>
                    </>
                  ) : (
                    <button type="button" className="btn btn-primary btn-block" style={{ minHeight: 44 }} disabled={(job?.status === 'building' || job?.status === 'queued')} onClick={() => requestReport(key, label, API_ENDPOINTS.CAMPUS_REPORT_STUDENT(id))}>{(job?.status === 'building' || job?.status === 'queued') ? 'Building — you can leave this page' : 'Build the PDF'}</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
